import { make } from 'vuex-pathify'
import store from '../index'
import { getQueryFromFilter, showError, showSuccess } from '@/util/helpers'
import { formatDate, removeFormatDate } from '@/util/formatters'
import axios from 'axios'

const state = {
  contract: {
    items: [],
  },

  contracts: {
    data: [],
    count: 0,
    limit: 50,
  },

  resumo: {
    contratosAtivos: 0,
    receitaRecorrenteMensal: 0,
    valorAFaturar: 0,
    valorFinanceiroEmAberto: 0,
  },

  eligibleServiceOrders: {
    data: [],
    count: 0,
  },

  activeReadjustmentContracts: {
    data: [],
    count: 0,
  },

  filter: {
    page: 1,
    itemsPerPage: 25,
    sortBy: ['createdAt'],
    sortDesc: [true],
    customerId: null,
    paymentMethodId: null,
    startDate: null,
    endDate: null,
  },

  type: 'add',
  loading: false,
  dialog: false,
  serviceOrderDialog: false,
  readjustmentDialog: false,
}

const mutations = make.mutations(state)

const actions = {
  add: ({ state }) => {
    state.contract = {
      items: [],
      type: 1,
      discount: 0,
      value: 0,
      companyId: store.get('authStore@currentCompany').id,
      userId: store.get('authStore@currentUser').id,
    }
    state.type = 'add'
    state.dialog = true

    store.dispatch('contractStore/loadCombos')
  },

  openGenerateServiceOrdersDialog: ({ state }) => {
    state.serviceOrderDialog = true
  },

  openReadjustmentDialog: ({ state }) => {
    state.readjustmentDialog = true
  },

  edit: ({ state }, id) => {
    state.loading = true
    store.dispatch('contractStore/getById', id)
      .then(res => {
        state.contract = {
          ...res.data,
          startDate: res.data.startDate ? formatDate(new Date(res.data.startDate).toISOString().substring(0, 10)) : null,
        }

        state.type = 'edit'
        state.dialog = true

        store.dispatch('contractStore/loadCombos')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  view: ({ state }, id) => {
    state.loading = true
    store.dispatch('contractStore/getById', id)
      .then(res => {
        state.contract = {
          ...res.data,
          startDate: res.data.startDate ? formatDate(new Date(res.data.startDate).toISOString().substring(0, 10)) : null,
        }

        state.type = 'view'
        state.dialog = true

        store.dispatch('contractStore/loadCombos')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  loadCombos: async () => {
    store.dispatch('customerStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })

    store.dispatch('userStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })

    store.dispatch('paymentMethodStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })

    store.dispatch('accountStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['description'],
      sortDesc: [false],
    })

    store.dispatch('serviceStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })
  },

  save: async ({ state, dispatch }, contract) => {
    const payload = {
      ...contract,
      startDate: contract.startDate ? removeFormatDate(contract.startDate) : null,
    }

    const pdfFile = payload.pdfFile
    delete payload.pdfFile

    const id = payload.id
    const method = id ? 'put' : 'post'

    state.loading = true
    try {
      const { data } = await axios[method](`${process.env.VUE_APP_API_URL}/contract/${id || ''}`, payload)
      const contractId = data.id || id
      state.contract.id = contractId

      if (pdfFile) {
        const uploadedPath = await dispatch('uploadPdf', { id: contractId, file: pdfFile, currentPath: payload.filePath })
        if (uploadedPath) {
          state.contract.filePath = uploadedPath
          await axios.put(`${process.env.VUE_APP_API_URL}/contract/${contractId}`, {
            ...payload,
            id: contractId,
            filePath: uploadedPath,
          })
        }
      }

      state.dialog = false
      await dispatch('get')
      showSuccess('Contrato salvo com sucesso!')
      return contractId
    } catch (e) {
      showError(e)
      throw e
    } finally {
      state.loading = false
    }
  },

  get: async ({ state }, filter = state.filter) => {
    state.loading = true
    return axios.get(`${process.env.VUE_APP_API_URL}/contract?${getQueryFromFilter(filter)}`)
      .then(res => {
        state.contracts = res.data
        state.resumo = res.data.resumo || state.resumo
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  getById: (context, id) => axios.get(`${process.env.VUE_APP_API_URL}/contract/${id}`).catch(showError),

  getCampagPontoPreview: (context, id) => axios
    .get(`${process.env.VUE_APP_API_URL}/contract/${id}/campag-ponto-preview`)
    .then(({ data }) => data),

  getCampagPontoCompanyPrices: (context, { contractId, companyId }) => axios
    .get(`${process.env.VUE_APP_API_URL}/contract/${contractId}/campag-ponto-companies/${encodeURIComponent(companyId)}/prices`)
    .then(({ data }) => data),

  saveCampagPontoCompanyPrices: (context, { contractId, companyId, useDefault, ranges }) => axios
    .put(`${process.env.VUE_APP_API_URL}/contract/${contractId}/campag-ponto-companies/${encodeURIComponent(companyId)}/prices`, { useDefault, ranges })
    .then(({ data }) => data),

  getEligibleForServiceOrders: async ({ state }) => {
    state.loading = true
    try {
      const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/contract/eligible-for-service-orders?${getQueryFromFilter()}`)
      state.eligibleServiceOrders = data
      return data
    } catch (e) {
      showError(e)
      throw e
    } finally {
      state.loading = false
    }
  },

  generateServiceOrders: async ({ state, dispatch }, contractIds) => {
    state.loading = true
    try {
      const { data } = await axios.post(
        `${process.env.VUE_APP_API_URL}/contract/generate-service-orders?${getQueryFromFilter()}`,
        { contractIds },
      )

      state.serviceOrderDialog = false
      await Promise.all([
        dispatch('get'),
        dispatch('getEligibleForServiceOrders'),
      ])
      showSuccess(`${data.requestIds.length} ordem(ns) de serviço gerada(s) com sucesso!`)
      return data
    } catch (e) {
      showError(e)
      throw e
    } finally {
      state.loading = false
    }
  },

  getActiveForReadjustment: async ({ state }) => {
    state.loading = true
    try {
      const { data } = await axios.get(
        `${process.env.VUE_APP_API_URL}/contract/active-for-readjustment?${getQueryFromFilter()}`,
      )
      state.activeReadjustmentContracts = data
      return data
    } catch (e) {
      showError(e)
      throw e
    } finally {
      state.loading = false
    }
  },

  recalculateContracts: async ({ state, dispatch }, contracts) => {
    state.loading = true
    try {
      const { data } = await axios.post(
        `${process.env.VUE_APP_API_URL}/contract/recalculate?${getQueryFromFilter()}`,
        { contracts },
      )

      state.readjustmentDialog = false
      await dispatch('get')
      showSuccess(`${data.count} contrato(s) recalculado(s) com sucesso!`)
      return data
    } catch (e) {
      showError(e)
      throw e
    } finally {
      state.loading = false
    }
  },

  remove: ({ state, dispatch }, id) => {
    state.loading = true
    return axios.delete(`${process.env.VUE_APP_API_URL}/contract/${id}`)
      .then(_ => {
        dispatch('get')
        showSuccess('Contrato excluído com sucesso!')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  async uploadPdf ({ state }, { id, file, currentPath = null }) {
    if (!id) {
      throw new Error('Salve o contrato antes de enviar o PDF')
    }

    if (currentPath) {
      try {
        await store.dispatch('fileStore/remove', { path: currentPath })
      } catch (_) {}
    }

    const { path } = await store.dispatch('fileStore/upload', {
      file,
      path: `contratos/${id}`,
    })

    state.contract.filePath = path
    return path
  },

  async openPdf (_, { filePath }) {
    if (!filePath) {
      throw new Error('PDF não encontrado')
    }

    const signedUrl = await store.dispatch('fileStore/get', { path: filePath })
    window.open(signedUrl, '_blank')
  },

  async generatePDF ({ state, dispatch }, { contract, openWhenExists = true }) {
    if (!contract?.id) {
      throw new Error('Salve o contrato antes de gerar o PDF')
    }

    if (contract.filePath) {
      if (openWhenExists) {
        await dispatch('openPdf', { filePath: contract.filePath })
      }
      showSuccess('O contrato já possui um PDF gerado')
      return contract.filePath
    }

    // Persist current dialog edits before the backend renders the saved contract.
    const payload = {
      ...contract,
      startDate: contract.startDate ? removeFormatDate(contract.startDate) : null,
    }
    await axios.put(`${process.env.VUE_APP_API_URL}/contract/${contract.id}`, payload)

    const response = await axios.get(`${process.env.VUE_APP_API_URL}/contract/${contract.id}/pdf?${getQueryFromFilter()}`, {
      responseType: 'arraybuffer',
      headers: { Accept: 'application/pdf' },
    })
    const contentType = String(response.headers?.['content-type'] || '').toLowerCase()
    if (contentType && !contentType.includes('application/pdf')) {
      throw new Error('O servidor não retornou um PDF válido')
    }
    const signature = new Uint8Array(response.data).slice(0, 4)
    if (signature.length !== 4 || signature[0] !== 0x25 || signature[1] !== 0x50 || signature[2] !== 0x44 || signature[3] !== 0x46) {
      throw new Error('O arquivo retornado não parece ser um PDF válido')
    }
    const file = new File([response.data], `contrato-${contract.id}.pdf`, {
      type: 'application/pdf',
    })

    const path = await dispatch('uploadPdf', {
      id: contract.id,
      file,
      currentPath: null,
    })

    await axios.patch(`${process.env.VUE_APP_API_URL}/contract/${contract.id}/pdf?${getQueryFromFilter()}`, {
      filePath: path,
    })

    state.contract.filePath = path
    await dispatch('get')
    await dispatch('openPdf', { filePath: path })
    showSuccess('PDF do contrato gerado com sucesso!')
    return path
  },

  async generateById ({ dispatch }, id) {
    const response = await dispatch('getById', id)
    const contract = {
      ...response.data,
      startDate: response.data.startDate ? formatDate(new Date(response.data.startDate).toISOString().substring(0, 10)) : null,
    }
    return dispatch('generatePDF', { contract })
  },
}

const getters = make.getters(state)

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
